// Statement Review & Approve Gate (D-9/D-10) — the B3 review screen, shared by
// the Monthly Cycle cockpit and the statement queue page. Deterministic (no AI
// dependency): every line shows the stored bill value beside the engine's
// freshly re-derived number and its plain-language source; B3 can write
// BINDING adjustments (reason required — applied by REGENERATING the bill,
// never by mutating it), reply on the Phoenix↔servicer return thread,
// explicitly APPROVE the review (the D-9 stamp), and only then send to the
// servicer. The stored-vs-computed divergence is always visible, never silent.

import { useState } from "react";
import { Button, StatusBadge, Textarea, Timestamp } from "@/design-system/components/ui";
import { fmtMoney } from "../paymentTracking/servicingFormat";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  loanStatementsApi,
  STATEMENT_STATUS_LABEL,
  type StatementReviewDto,
  type StatementReviewLineDto,
} from "@/api/portfolioAdmin/loanStatementsApi";
import {
  useAddStatementComment,
  useApproveStatementReview,
  useRegenerateStatementFromQueue,
  useRemoveStatementAdjustment,
  useSendStatementToTpsFromQueue,
  useStatementReview,
  useUpsertStatementAdjustment,
} from "@/hooks/portfolioAdmin/useLoanStatements";
import { ReportPdfFrame } from "../reports/ReportPdfFrame";
import { monthLabel } from "./cycleCopy";

export function StatementReviewModal({
  loanId,
  statementId,
  period,
  tpsName,
  open,
  onClose,
}: {
  loanId: string;
  statementId: string;
  period: string;
  tpsName: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  // A regenerate supersedes the row — the modal follows the fresh statement.
  const [activeId, setActiveId] = useState(statementId);
  const review = useStatementReview(open ? activeId : null);
  const [sent, setSent] = useState(false);

  const approve = useApproveStatementReview();
  const send = useSendStatementToTpsFromQueue();
  const regenerate = useRegenerateStatementFromQueue();

  const data = review.data;
  const month = monthLabel(period);
  const servicer = data?.tpsName ?? tpsName ?? "the servicing platform";
  const isPending = data?.statementStatus === "PendingReview";
  const approved = data?.reviewApprovedAt != null;
  const busy = approve.isPending || send.isPending || regenerate.isPending;
  // Why the send is blocked, in plain language — for the button's hover
  // tooltip and a visible caption. Only for the FIXABLE reasons (approve /
  // apply changes); a mid-action busy state needs no explanation.
  const sendBlockedReason = busy
    ? undefined
    : !approved
      ? "Approve the review before sending"
      : data?.hasUnappliedChanges
        ? "Apply your changes before sending"
        : undefined;

  const doApprove = async () => {
    try {
      await approve.mutateAsync({ statementId: activeId, loanId });
      toast.success("Review approved — you can now send it to the servicer.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const doSend = async () => {
    try {
      await send.mutateAsync({ statementId: activeId, loanId });
      setSent(true);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const doRegenerate = async () => {
    try {
      const fresh = await regenerate.mutateAsync({ statementId: activeId, loanId });
      setActiveId(fresh.id);
      toast.success("Regenerated — the adjustments are folded into the fresh bill below.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Review the ${month} statement`}
      footer={
        sent ? (
          <Button onClick={onClose} data-testid="review-done">
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Close
            </Button>
            {isPending && !approved && (
              <Button
                onClick={() => void doApprove()}
                loading={approve.isPending}
                disabled={busy || data?.hasUnappliedChanges}
                data-testid="review-approve"
              >
                Approve review
              </Button>
            )}
            {isPending && (
              <div className="flex flex-col items-end gap-space-1">
                <Button
                  onClick={() => void doSend()}
                  loading={send.isPending}
                  disabled={busy || !approved || data?.hasUnappliedChanges}
                  title={sendBlockedReason}
                  data-testid="review-send"
                >
                  Send to servicer
                </Button>
                {sendBlockedReason && (
                  <span
                    className="text-caption text-gray-mid"
                    data-testid="send-blocked-caption"
                  >
                    {sendBlockedReason}
                  </span>
                )}
              </div>
            )}
          </>
        )
      }
    >
      {sent ? (
        <p className="text-body text-onyx m-0" data-testid="review-sent">
          Sent — {servicer} now reviews and posts it to the borrower. If
          something looks wrong they return it to you with a note instead.
        </p>
      ) : review.isLoading || !data ? (
        <p className="text-small text-gray-mid m-0" data-testid="review-loading">
          {review.isError ? "Couldn't load the review. Close and retry." : "Loading the review…"}
        </p>
      ) : (
        <div className="flex flex-col gap-space-4" data-testid="statement-review-modal">
          <div className="flex flex-wrap items-center justify-between gap-space-2">
            <p className="text-small text-gray-mid m-0">
              {data.loanNumber} · Servicer: {servicer}
            </p>
            <StatusBadge tone={data.statementStatus === "Returned" ? "danger" : "warn"}>
              {STATEMENT_STATUS_LABEL[data.statementStatus]}
            </StatusBadge>
          </div>

          {data.returnedReason && (
            <div
              role="note"
              className="rounded-md border border-status-error bg-status-error/10 p-space-3 text-small text-onyx"
              data-testid="review-returned-note"
            >
              <span className="font-semibold">The servicer returned this bill:</span>{" "}
              {data.returnedReason}
            </div>
          )}

          {data.hasUnappliedChanges && (
            <div
              className="rounded-md border border-status-warning bg-status-warning/10 p-space-3"
              data-testid="review-delta-banner"
            >
              <p className="text-small text-onyx m-0">
                <span className="font-semibold">Unapplied changes.</span> This
                bill shows {fmtMoney(data.amountDue)}, but regenerating
                now would bill{" "}
                {data.regeneratedAmountDue != null
                  ? fmtMoney(data.regeneratedAmountDue)
                  : "a different total"}
                . Adjustments and fee changes only become binding when the bill
                is regenerated — nothing is edited in place.
              </p>
              {data.canAdjust && (
                <div className="mt-space-2">
                  <Button
                    size="sm"
                    onClick={() => void doRegenerate()}
                    loading={regenerate.isPending}
                    disabled={busy}
                    data-testid="review-regenerate"
                  >
                    Apply & regenerate
                  </Button>
                </div>
              )}
            </div>
          )}

          {!data.hasUnappliedChanges &&
            data.computedAmountDue != null &&
            data.amountDue !== data.computedAmountDue && (
              <p
                className="text-small text-onyx m-0 rounded-md border border-border-light bg-white-warm p-space-3"
                data-testid="review-adjusted-delta"
              >
                This bill's total {fmtMoney(data.amountDue)} deliberately
                differs from the engine-computed{" "}
                {fmtMoney(data.computedAmountDue)} (Δ{" "}
                {fmtMoney(data.amountDue - data.computedAmountDue)})
                because of the binding adjustments below.
              </p>
            )}

          <ReviewLinesTable review={data} loanId={loanId} />

          <div className="flex items-baseline justify-between border border-border-light rounded-md px-space-3 py-space-2 bg-white-warm">
            <span className="text-small text-gray-mid">Total amount due</span>
            <span className="font-semibold text-h3 text-onyx" data-testid="review-amount-due">
              {fmtMoney(data.amountDue)}
            </span>
          </div>

          <ReviewThread review={data} loanId={loanId} />

          {/* The real statement PDF — the SAME artifact the servicer approves
              and the borrower sees. Load failure is non-fatal. */}
          <div className="flex flex-col gap-space-2" data-testid="review-view-statement">
            <span className="text-small font-semibold text-onyx">
              The bill as the servicer sees it
            </span>
            <ReportPdfFrame
              src={loanStatementsApi.previewUrl(activeId)}
              testId="review-statement-frame"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

function ReviewLinesTable({ review, loanId }: { review: StatementReviewDto; loanId: string }) {
  const toast = useToast();
  const upsert = useUpsertStatementAdjustment();
  const remove = useRemoveStatementAdjustment();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const startEdit = (line: StatementReviewLineDto) => {
    setEditingKey(line.key);
    setAmount(String(line.adjustedAmount ?? line.statementAmount));
    setReason(line.adjustmentReason ?? "");
  };

  const save = async (lineKey: string) => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Enter a non-negative dollar amount.");
      return;
    }
    if (reason.trim().length === 0) {
      toast.error("A reason is required for a binding adjustment.");
      return;
    }
    try {
      await upsert.mutateAsync({
        statementId: review.statementId,
        loanId,
        lineKey,
        adjustedAmount: parsed,
        reason: reason.trim(),
      });
      setEditingKey(null);
      toast.success("Adjustment saved — apply it by regenerating the bill.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const removeAdjustment = async (lineKey: string) => {
    if (reason.trim().length === 0) {
      toast.error("A reason is required to remove an adjustment.");
      return;
    }
    try {
      await remove.mutateAsync({
        statementId: review.statementId,
        loanId,
        lineKey,
        reason: reason.trim(),
      });
      setEditingKey(null);
      toast.success("Adjustment removed — regenerate to return to the computed value.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const busy = upsert.isPending || remove.isPending;

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-small" data-testid="review-lines">
        <thead>
          <tr className="text-left text-caption uppercase tracking-label text-gray-mid">
            <th className="py-space-1 pr-space-3 font-semibold">Line</th>
            <th className="py-space-1 pr-space-3 font-semibold text-right">On this bill</th>
            <th className="py-space-1 pr-space-3 font-semibold text-right">Computed now</th>
            <th className="py-space-1 pr-space-3 font-semibold text-right">Adjusted to</th>
            <th className="py-space-1 font-semibold">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {review.lines.map((line) => (
            <ReviewLineRow
              key={line.key}
              line={line}
              canAdjust={review.canAdjust}
              editing={editingKey === line.key}
              amount={amount}
              reason={reason}
              busy={busy}
              onAmount={setAmount}
              onReason={setReason}
              onStartEdit={() => startEdit(line)}
              onCancel={() => setEditingKey(null)}
              onSave={() => void save(line.key)}
              onRemove={() => void removeAdjustment(line.key)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReviewLineRow({
  line,
  canAdjust,
  editing,
  amount,
  reason,
  busy,
  onAmount,
  onReason,
  onStartEdit,
  onCancel,
  onSave,
  onRemove,
}: {
  line: StatementReviewLineDto;
  canAdjust: boolean;
  editing: boolean;
  amount: string;
  reason: string;
  busy: boolean;
  onAmount: (v: string) => void;
  onReason: (v: string) => void;
  onStartEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onRemove: () => void;
}) {
  return (
    <>
      <tr
        className="border-t border-border-light align-top"
        data-testid={`review-line-${line.key}`}
      >
        <td className="py-space-2 pr-space-3">
          <span className="text-onyx font-medium">{line.label}</span>
          <span className="block text-caption text-gray-mid max-w-reading">{line.source}</span>
        </td>
        <td className="py-space-2 pr-space-3 text-right tabular-nums">
          {fmtMoney(line.statementAmount)}
        </td>
        <td className="py-space-2 pr-space-3 text-right tabular-nums text-gray-mid">
          {line.computedAmount != null ? fmtMoney(line.computedAmount) : "—"}
        </td>
        <td className="py-space-2 pr-space-3 text-right tabular-nums">
          {line.adjustedAmount != null ? (
            <span
              className="font-semibold text-gold-shadow"
              title={line.adjustmentReason ?? undefined}
            >
              {fmtMoney(line.adjustedAmount)}
            </span>
          ) : (
            "—"
          )}
        </td>
        <td className="py-space-2 text-right">
          {line.adjustable && canAdjust && !editing && (
            <Button
              variant="text"
              size="sm"
              onClick={onStartEdit}
              disabled={busy}
              data-testid={`review-adjust-${line.key}`}
            >
              Adjust
            </Button>
          )}
        </td>
      </tr>
      {editing && (
        <tr className="border-t border-border-light bg-white-warm">
          <td colSpan={5} className="p-space-3">
            <div className="flex flex-col gap-space-2 max-w-reading">
              <label className="flex flex-col gap-space-1 text-small">
                <span className="text-caption uppercase tracking-label text-gray-mid">
                  Adjusted amount ($)
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => onAmount(e.target.value)}
                  className="border border-border-light rounded-md px-space-2 py-space-1 bg-white-pure w-48"
                  data-testid="review-adjust-amount"
                />
              </label>
              <label className="flex flex-col gap-space-1 text-small">
                <span className="text-caption uppercase tracking-label text-gray-mid">
                  Reason (required — this is a binding change to what gets billed)
                </span>
                <Textarea
                  value={reason}
                  onChange={(e) => onReason(e.target.value)}
                  rows={2}
                  data-testid="review-adjust-reason"
                />
              </label>
              <div className="flex flex-wrap gap-space-2">
                <Button size="sm" onClick={onSave} disabled={busy} data-testid="review-adjust-save">
                  Save adjustment
                </Button>
                {line.adjustedAmount != null && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={onRemove}
                    disabled={busy}
                    data-testid="review-adjust-remove"
                  >
                    Remove adjustment
                  </Button>
                )}
                <Button size="sm" variant="text" onClick={onCancel} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ReviewThread({ review, loanId }: { review: StatementReviewDto; loanId: string }) {
  const toast = useToast();
  const { tz } = useDateFormatter();
  const addComment = useAddStatementComment();
  const [reply, setReply] = useState("");

  // The thread renders once there is (or has been) a conversation — i.e. any
  // comment on the chain, or the bill is currently returned.
  if (review.comments.length === 0 && review.statementStatus !== "Returned") return null;

  const sendReply = async () => {
    if (reply.trim().length === 0) return;
    try {
      await addComment.mutateAsync({
        statementId: review.statementId,
        loanId,
        body: reply.trim(),
      });
      setReply("");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <div className="flex flex-col gap-space-2" data-testid="review-thread">
      <span className="text-small font-semibold text-onyx">
        Conversation with the servicer
      </span>
      {review.comments.length === 0 ? (
        <p className="text-small text-gray-mid m-0">No notes yet.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-space-2 p-0">
          {review.comments.map((c) => (
            <li
              key={c.id}
              className="rounded-md border border-border-light bg-white-warm p-space-3"
              data-testid={`review-comment-${c.id}`}
            >
              <p className="m-0 text-caption uppercase tracking-label text-gray-mid">
                {c.authorRole === "servicer" ? `Servicer — ${c.authorName ?? ""}` : c.authorName ?? "Phoenix"}
                {" · "}
                <Timestamp value={c.createdAt} tz={tz} />
              </p>
              <p className="m-0 mt-space-1 text-small text-onyx whitespace-pre-wrap">{c.body}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-space-2">
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={2}
          placeholder="Reply to the servicer (visible in their portal)…"
          data-testid="review-reply-input"
        />
        <div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void sendReply()}
            disabled={addComment.isPending || reply.trim().length === 0}
            loading={addComment.isPending}
            data-testid="review-reply-send"
          >
            Reply
          </Button>
        </div>
      </div>
    </div>
  );
}
